/**
 * RecentSearchesRail — the Research dashboard's history rail.
 *
 * Modelled on XoeConversationRail: a compact selectable list of the practitioner's
 * recent research searches. Clicking a row re-runs that query (onSelect); each row has a
 * delete control, and a "Clear all" action empties the history. All colour via --mg-* tokens.
 */

import React from 'react';
import { List, Typography, Button, Tooltip, Popconfirm } from 'antd';
import { DeleteOutlined, ClearOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { SectionCard, StatusTag, EmptyState, InlineLoading } from '../../components/shared';
import {
	useResearchHistory,
	useDeleteResearchSearch,
	useClearResearchSearches,
} from '../../hooks/useResearch';
import type { ResearchSearch, ResearchSearchSource } from '../../types/research/ResearchTypes';

dayjs.extend(relativeTime);

const { Text } = Typography;

const SOURCE_LABEL: Record<ResearchSearchSource, string> = {
	knowledge_base: 'Knowledge',
	literature: 'Literature',
	all: 'All',
};

const SOURCE_COLOR: Record<ResearchSearchSource, string> = {
	knowledge_base: 'var(--mg-primary)',
	literature: 'var(--mg-info)',
	all: 'var(--mg-warning)',
};

export interface RecentSearchesRailProps {
	/** Re-run this search (sets the query + tab on the dashboard). */
	onSelect: (search: ResearchSearch) => void;
}

const RecentSearchesRail: React.FC<RecentSearchesRailProps> = ({ onSelect }) => {
	const { data: searches, isLoading } = useResearchHistory({ limit: 20 });
	const deleteSearch = useDeleteResearchSearch();
	const clearSearches = useClearResearchSearches();

	const hasItems = !!searches && searches.length > 0;

	return (
		<SectionCard
			// A full-height pane whose list scrolls internally. Without this the 20-row list
			// grows the card past its grid track and spills down the page.
			className="mg-research-pane mg-research-pane--flush"
			title="Recent searches"
			extra={
				hasItems ? (
					<Popconfirm
						title="Clear all recent searches?"
						okText="Clear"
						okButtonProps={{ danger: true }}
						cancelText="Cancel"
						onConfirm={() => clearSearches.mutate()}
					>
						<Tooltip title="Clear all">
							<Button
								type="text"
								size="small"
								icon={<ClearOutlined />}
								loading={clearSearches.isPending}
								aria-label="Clear all recent searches"
								data-testid="research-history-clear"
							/>
						</Tooltip>
					</Popconfirm>
				) : undefined
			}
			noPadding
		>
			{isLoading ? (
				<InlineLoading minHeight={160} />
			) : !hasItems ? (
				<EmptyState
					icon={<HistoryOutlined style={{ fontSize: 28, color: 'var(--mg-text-secondary)', opacity: 0.4 }} />}
					title="No recent searches"
					description="Your searches will appear here."
				/>
			) : (
				<List
					dataSource={searches}
					split={false}
					renderItem={(s) => (
						<List.Item
							key={s.id}
							onClick={() => onSelect(s)}
							style={{
								padding: 'var(--mg-sp-3) var(--mg-sp-4)',
								cursor: 'pointer',
								borderLeft: '3px solid transparent',
								transition: 'background var(--mg-dur-fast) var(--mg-ease)',
							}}
							data-testid={`research-history-item-${s.id}`}
						>
							<div style={{ width: '100%', minWidth: 0 }}>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 4,
									}}
								>
									<Text
										ellipsis
										style={{ fontSize: 'var(--mg-fs-md)', flex: 1, minWidth: 0 }}
									>
										{s.query}
									</Text>
									<Tooltip title="Delete">
										<Button
											type="text"
											size="small"
											icon={<DeleteOutlined />}
											aria-label="Delete recent search"
											data-testid={`research-history-delete-${s.id}`}
											onClick={(e) => {
												e.stopPropagation();
												deleteSearch.mutate({ id: s.id });
											}}
										/>
									</Tooltip>
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
									<StatusTag
										status={s.source}
										label={SOURCE_LABEL[s.source] ?? s.source}
										color={SOURCE_COLOR[s.source] ?? 'var(--mg-primary)'}
										size="small"
									/>
									<Text type="secondary" style={{ fontSize: 'var(--mg-fs-xs)' }}>
										{dayjs(s.created_at).fromNow()}
									</Text>
								</div>
							</div>
						</List.Item>
					)}
				/>
			)}
		</SectionCard>
	);
};

export default RecentSearchesRail;
